import React, { type FC, type PropsWithChildren } from 'react';

import type { Lucid } from '../../types/types';
import Box from '../box/box';
import Container from '../container/container';
import Heading from '../heading/heading';
import Link from '../link/link';

export interface AdditionalResourcesProps {
  blurb?: string | null | undefined;
  blurbText?: string;
  /** Allows the component to get additional styles. */
  className?: string;
  contactSalesText?: string;
  ctaPath?: string | undefined;
  resources: Lucid.Directus.Generated.Links[];
  sectionHeading?: string;
}

const AdditionalResources: FC<PropsWithChildren<AdditionalResourcesProps>> = ({
  blurb,
  blurbText,
  className,
  contactSalesText,
  ctaPath,
  resources,
  sectionHeading,
}) => (
  <Box
    as="section"
    css={theme => ({
      label: 'additionalResources',
      backgroundColor: theme.colors.coolGrey[90],
      backgroundImage:
        'url("https://cdn-cashy-static-assets.lucidchart.com/marketing/pages/chart/consideration/background/footer-background.png")',
      backgroundPositionX: '-120px',
      backgroundPositionY: '40%',
      backgroundSize: '100%',
      color: theme.colors.white,
      padding: `${theme.space.s64}px 0`,
    })}
    // eslint-disable-next-line @stylistic/jsx-sort-props
    className={className}
  >
    <Container css={{ display: 'flex', flexWrap: 'wrap' }}>
      <Box
        css={theme => ({
          flex: '0 1 100%',
          marginBottom: theme.space.s24,
          [theme.breakpoints.bp768.min]: {
            flex: '0 1 41.6%',
          },
        })}
      >
        <Heading
          as="h2"
          css={theme => ({
            color: theme.colors.white,
            marginTop: 0,
          })}
          variant="headingM"
        >
          {sectionHeading}
        </Heading>
        <ul css={{ margin: 0, padding: 0, listStyle: 'none' }}>
          {resources
            .filter(ar => ar.default_link_text)
            .map(ar => (
              <li key={ar.id}>
                <Link
                  css={theme => ({
                    marginBottom: theme.space.s8,
                    fontSize: theme.fontSizes.fs20,
                  })}
                  href={ar.final_url}
                  iconAnimation="navigational"
                  variant="secondaryInverse"
                >
                  {ar.default_link_text}
                </Link>
              </li>
            ))}
        </ul>
      </Box>
      <Box
        css={theme => ({
          flex: '0 1 100%',
          [theme.breakpoints.bp768.min]: {
            flex: '0 1 55.3%',
          },
        })}
      >
        <Heading
          as="h2"
          css={theme => ({
            color: 'white',
            marginTop: theme.space.s32,
            marginBottom: theme.space.s16,
            [theme.breakpoints.bp768.min]: {
              marginTop: 0,
            },
          })}
          variant="headingXL"
        >
          {blurb || blurbText}
        </Heading>
        {ctaPath && contactSalesText && (
          <Link displayAsButton href={ctaPath} variant="secondary">
            {contactSalesText}
          </Link>
        )}
      </Box>
    </Container>
  </Box>
);

export default AdditionalResources;
